import axios from 'axios'

const BASE_PATH = '/api/admin/users/external'
// Mutation timeout — matches the Admin's POST-upload convention (1000 * 60 * 2). Without it a
// hung backend/network stalls the create dialog indefinitely. Kept generous (not a snappy 15s)
// because this is a provisioning mutation: a premature client timeout could falsely report
// failure for a user that was actually created. On timeout axios throws with no `response`, so
// it surfaces through the catch (status undefined) as the failure dialog with "Try again".
const CREATE_TIMEOUT_MS = 1000 * 60 * 2

// Client-synthesized errorCode (NOT a backend code): emitted when a 2xx create response omits the
// one-time tempPassword, so the caller branches on it instead of matching a raw string in two files.
export const MISSING_CREDENTIALS_ERROR_CODE = 'MISSING_CREDENTIALS'

/**
 * Transport for external-user (Non-Microsoft / CIAM) admin operations. Owns the endpoint
 * paths and normalizes the server's `{ errorCode, error }` envelope into a flat result the
 * caller can switch on. This layer is transport only — UI concerns (mapping `errorCode` to a
 * form field, toast vs. failure dialog, user-facing fallback copy) stay in the view.
 *
 * Matches the `src/services` singleton convention (see SecurityService). Create is the only
 * operation today; update / delete / reset-password (W2.4–W2.7) extend this class.
 */
class ExternalUserService {
  static instance

  static singletonInstance() {
    if (!ExternalUserService.instance) {
      ExternalUserService.instance = new ExternalUserService()
    }
    return ExternalUserService.instance
  }

  /**
   * POST /api/admin/users/external — provision a Non-Microsoft user.
   *
   * @param   {{ username: string, displayName: string, mail?: string, sendInvite?: boolean }} input
   *          `mail` is optional; blank/whitespace is dropped (not sent). `sendInvite` requests the
   *          create endpoint email the temp credentials to `mail` instead of only returning them.
   * @returns {Promise<
   *   | { ok: true, data: { _id: string, oid: string, userType: string, displayName: string,
   *                         username: string, mail: string|null, tempPassword: string,
   *                         emailSent: boolean } }
   *   | { ok: false, status?: number, errorCode?: string, message?: string }
   * >} `ok:true` carries the created user's read-safe projection plus the one-time credentials,
   *    and is returned ONLY when `tempPassword` is present. `_id`/`oid`/`userType` are what a
   *    caller needs to hand the created user straight to `UserDetailsDialog`; callers that do
   *    should still verify them rather than assume, since only `tempPassword` is enforced here.
   *    `ok:false` carries the normalized error — `status` (HTTP), `errorCode`
   *    (SCREAMING_SNAKE), and `message` (server's English copy, if any). A network error (no
   *    response) yields `ok:false` with `status` undefined; a 2xx that omits `tempPassword` yields
   *    `ok:false` with `errorCode: 'MISSING_CREDENTIALS'`.
   */
  async createUser({ username, displayName, mail, sendInvite }) {
    const body = {
      username,
      displayName: typeof displayName === 'string' ? displayName.trim() : displayName,
      sendInvite: Boolean(sendInvite)
    }
    const trimmedMail = typeof mail === 'string' ? mail.trim() : ''
    if (trimmedMail.length > 0) body.mail = trimmedMail

    try {
      const response = await axios.post(BASE_PATH, body, { timeout: CREATE_TIMEOUT_MS })
      const data = response?.data?.data
      // A 2xx that doesn't carry the one-time `tempPassword` means the secret was lost in transit
      // (backend/middleware bug or response-shape drift). It can never be re-fetched, so report a
      // failure rather than letting the caller open a blank credentials modal and strand the admin.
      if (!data?.tempPassword) {
        return { ok: false, status: response?.status, errorCode: MISSING_CREDENTIALS_ERROR_CODE }
      }
      return { ok: true, data }
    } catch (error) {
      const data = error?.response?.data
      return { ok: false, status: error?.response?.status, errorCode: data?.errorCode, message: data?.error }
    }
  }

  /**
   * POST /api/admin/users/external/:id/reset-password — mint a new temporary password.
   *
   * Both strategies return the password: `SEND_EMAIL` additionally mails it to the user, it does not
   * withhold it from the admin. `emailSent` reports whether that dispatch was attempted, so the
   * caller can pick the success copy without re-deriving it from email presence.
   *
   * @param   {string} externalUserId  Mongo `_id` of the external user
   * @param   {'SEND_EMAIL'|'SHOW_ADMIN'} strategy
   * @returns {Promise<
   *   | { ok: true, data: { displayName?: string, username?: string, mail?: string,
   *                         tempPassword: string, emailSent: boolean } }
   *   | { ok: false, status?: number, errorCode?: string, message?: string }
   * >} Same envelope as `createUser`; a 2xx without `tempPassword` yields `errorCode:
   *    'MISSING_CREDENTIALS'`.
   */
  /**
   * PATCH /api/admin/users/external/:id — update mutable external-user fields (min one key).
   *
   * `displayName`/`mail`/`username` are accepted server-side; callers should send just the
   * field(s) they intend to change.
   *
   * @param   {string} externalUserId  Mongo `_id` of the external user
   * @param   {{ displayName?: string, mail?: string, username?: string }} patch
   * @returns {Promise<
   *   | { ok: true, data: object }
   *   | { ok: false, status?: number, errorCode?: string, message?: string }
   * >} Same normalized envelope as `createUser`/`resetPassword`.
   */
  async updateUser(externalUserId, { displayName, mail, username } = {}) {
    const body = {}
    if (typeof displayName === 'string') body.displayName = displayName.trim()
    if (typeof mail === 'string') body.mail = mail.trim()
    if (typeof username === 'string' && username.trim().length > 0) body.username = username.trim()

    try {
      // Same timeout as create/reset — this is also a Graph-backed provisioning mutation.
      const response = await axios.patch(`${BASE_PATH}/${externalUserId}`, body, { timeout: CREATE_TIMEOUT_MS })
      return { ok: true, data: response?.data?.data }
    } catch (error) {
      const data = error?.response?.data
      return { ok: false, status: error?.response?.status, errorCode: data?.errorCode, message: data?.error }
    }
  }

  async resetPassword(externalUserId, strategy) {
    try {
      const response = await axios.post(
        `${BASE_PATH}/${externalUserId}/reset-password`,
        { strategy },
        { timeout: CREATE_TIMEOUT_MS }
      )
      const data = response?.data?.data
      if (!data?.tempPassword) {
        return { ok: false, status: response?.status, errorCode: MISSING_CREDENTIALS_ERROR_CODE }
      }
      return { ok: true, data }
    } catch (error) {
      const data = error?.response?.data
      return { ok: false, status: error?.response?.status, errorCode: data?.errorCode, message: data?.error }
    }
  }

  /**
   * POST /api/external-auth/session — bootstrap an external (CIAM) user's Admin session from the
   * HttpOnly `idTokenAdmin` cookie the sign-in flow sets for dest=admin (shared domain in
   * production, see `routes/externalAuthRoute.js`). NOT under `BASE_PATH` — this is a pre-auth
   * session-bootstrap route, not an authenticated external-user CRUD operation.
   *
   * @param   {string} [idToken]  Slot-env fallback only — production relies on the shared-domain
   *   cookie via `withCredentials`. Slot has no shared domain, so App.js passes the token it
   *   cookied from the callback's URL param; the route reads `body.idToken` for exactly this case.
   * @returns {Promise<
   *   | { ok: true, data: { message: string, userData: object, accessToken: string, exp: number } }
   *   | { ok: false, status?: number, errorCode?: string, message?: string }
   * >} `data` matches the same envelope `POST /api/authorize/checkWebUser` returns for a workforce
   *    session, so the caller can run both through the same role-gate logic.
   */
  async establishSession(idToken) {
    try {
      const body = idToken ? { idToken } : {}
      const response = await axios.post('/api/external-auth/session', body, {
        withCredentials: true,
        timeout: CREATE_TIMEOUT_MS
      })
      return { ok: true, data: response?.data }
    } catch (error) {
      const data = error?.response?.data
      return { ok: false, status: error?.response?.status, errorCode: data?.errorCode, message: data?.error }
    }
  }
}

export default ExternalUserService
